import Head from 'next/head';

import { Maybe, Page } from '@/types/generated';
import { useAppContext } from '@/context/AppContextProvider.ctx';
import { NextPageHeadSchema } from '@/components/NextPage/NextPageHeadSchema';

export default function NextPageHead({
	title,
	indexed,
	description,
	canonical,
	breadcrumbs,
	breadcrumbTitle,
	path,
	originUrl,
	ogImage,
	contentsCollection,
}: Partial<Page> & { breadcrumbs?: Maybe<Page>[]; originUrl: string; ogImage: string }) {
	const { config } = useAppContext();
	const { salesForceEvergageScript } = config.featureFlags;
	const { schemaUrl } = config;

	let formattedCanonical = canonical;
	if (canonical && !canonical.endsWith('/')) {
		formattedCanonical = `${canonical}/`;
	}

	const ogUrl = formattedCanonical ?? schemaUrl;

	return (
		<>
			<Head>
				<title>{title}</title>
				{!indexed && <meta key="robots" name="robots" content="noindex,nofollow" />}
				{!indexed && <meta key="googlebot" name="googlebot" content="noindex,nofollow" />}
				{description && <meta name="description" content={description} />}
				{canonical ? (
					<link
						rel="canonical"
						href={canonical.substring(canonical.length - 1) !== '/' ? `${canonical}/` : canonical}
					/>
				) : (
					<link
						rel="canonical"
						href={
							path?.substring(path.length - 1) !== '/'
								? `${originUrl}${path as string}/`
								: `${originUrl}${path}`
						}
					/>
				)}
				{/* OG Tags */}
				<meta property="og:image" content={ogImage} />
				<meta property="og:image:secure_url" content={ogImage} />
				<meta property="og:title" content={title ?? ''} />
				<meta property="og:description" content={description ?? ''} />
				<meta property="og:url" content={ogUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:image" content={ogImage} />
				<meta name="twitter:title" content={title ?? ''} />
				<meta name="twitter:description" content={description ?? ''} />
				<meta name="twitter:url" content={canonical ?? schemaUrl} />
				{salesForceEvergageScript === 'Async' ? (
					<script
						type="text/javascript"
						async
						src="//cdn.evgnet.com/beacon/aspendentalmgmtinc2/engage/scripts/evergage.min.js"
					/>
				) : salesForceEvergageScript === 'Sync' ? (
					<script
						type="text/javascript"
						src="//cdn.evgnet.com/beacon/aspendentalmgmtinc2/engage/scripts/evergage.min.js"
					/>
				) : null}
			</Head>
			<NextPageHeadSchema
				breadcrumbs={breadcrumbs}
				breadcrumbTitle={breadcrumbTitle as Maybe<Page['breadcrumbTitle']>}
				originUrl={originUrl}
				title={title as Maybe<Page['title']>}
				description={description as Maybe<Page['description']>}
				contentsCollection={contentsCollection as Maybe<Page['contentsCollection']>}
				path={path as Maybe<Page['path']>}
			/>
		</>
	);
}
